import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, take, tap } from 'rxjs';

import { SessionStorageService } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';
import { createToast } from '../notifications/ninety-toast-options';
import { NotificationActions } from '../notifications/notification.actions';
import { NavigationService } from '../router/navigation.service';

import { CommandRunner } from './command-runner.service';
import { SessionActions } from './session.actions';

/**
 * The Commands that tell the rest of the app what the machine decided, and the one app action that
 * tells the machine something back.
 *
 * These go through `CommandRunner.issued` rather than `on`, because none of them has an outcome the
 * machine makes a decision about: the app is being told, not asked.
 *
 * ## Why the app hears about a session through Commands at all
 *
 * `initGlobalState` and `resetGlobalState` are what every other slice keys off, and they are issued
 * from a transition rather than dispatched by whoever caused the sign-in. That is what stops the
 * two from disagreeing: there is one place that decides a session started, and everything else
 * reads its answer.
 *
 * By the time any of these runs the state has already advanced — `stateAdvanced` is dispatched
 * before any `commandIssued` — so an effect reacting to `initGlobalState` reads the new Resource
 * Tokens off the slice, not the previous ones.
 */
@Injectable()
export class SessionAppEffects {
  private readonly actions$ = inject(Actions);
  private readonly commands = inject(CommandRunner);
  private readonly nav = inject(NavigationService);
  private readonly storage = inject(SessionStorageService);

  /**
   * The three places a launch or a sign-out can land, as a router navigation.
   *
   * Sign-out in particular: a reload costs 1-2s of splash screen and is unavailable offline, and
   * neither is acceptable for something someone just asked for. This is why `retainedStateForReset`
   * carries the auth slice across the reset — the machine orchestrating the sign-out is still
   * running afterwards.
   */
  readonly navigate$ = createEffect(
    () =>
      this.commands.issued('navigate').pipe(
        tap(({ to }) => {
          if (to === 'home') this.nav.goToHome();
          else if (to === 'offline') this.nav.goToOffline();
          else this.nav.goToLogin();
        })
      ),
    { dispatch: false }
  );

  /** The one Command that reloads, and only a Company Switch produces it. */
  readonly reloadApp$ = createEffect(() => this.commands.issued('reloadApp').pipe(map(() => appActions.reloadApp())));

  readonly signedIn$ = createEffect(() =>
    this.commands.issued('notifyAppSignedIn').pipe(map(() => appActions.initGlobalState()))
  );

  readonly signedOut$ = createEffect(() =>
    this.commands.issued('notifyAppSignedOut').pipe(map(({ reason }) => SessionActions.signedOut({ reason })))
  );

  /**
   * A Company Switch, as an auth event.
   *
   * It is an auth event because it re-mints Resource Tokens, which only the machine may do — and
   * routing it through `refreshing` is what gives it single-flight for free, so two switches cannot
   * overlap and a switch cannot race an ordinary refresh.
   *
   * The remembered team goes here because it is company-scoped, and the switch ends in a reload
   * that would otherwise read it back under the new company.
   */
  readonly switchCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.switchCompany),
      map(({ companyUserId }) => {
        this.storage.delete('lastAccessedTeamId');
        return SessionActions.eventReceived({ event: { type: 'companySwitchRequested', companyUserId } });
      })
    )
  );

  /**
   * A retry that changed nothing, said out loud.
   *
   * A successful retry is self-announcing: it ends on `authorized` and the navigation home is the
   * feedback. A failed one is invisible — the machine leaves `restoreDeferred`, fails, and lands
   * back in a state identical to the one it left, so nothing on screen moves and the tap reads as a
   * button that does not work. On the `network` path that is the common case, because nothing is
   * coming to fix it on its own.
   *
   * **Only an explicit retry is announced.** `connectivityRegained` fires whenever the OS reports a
   * path, which on a flapping connection is often and unprompted; toasting those would interrupt
   * someone who asked for nothing. `retryRequested` exists precisely because a person asked, and a
   * person who asked is owed an answer.
   *
   * **The tap can be swallowed, and a swallowed tap has no outcome to report.** `refreshRequested`
   * is a silent no-op — no `stateAdvanced` at all — when it lands in `refreshing` (single-flight) or
   * in `authorized` outside the refresh window. If a `connectivityRegained` refresh beat this tap to
   * `restoreDeferred` by a few hundred milliseconds, the tap did nothing, and this effect must not
   * borrow whatever `stateAdvanced` happens to arrive next — that advance belongs to some other
   * event, and toasting it would surprise someone about a tap they have since forgotten.
   *
   * So the wait is two steps, not one. The first advance after the tap is checked for `refreshing`:
   * that is the machine confirming the tap actually started a restore. Only then is the *next*
   * advance the tap's own outcome, and `restoreDeferred` there means it failed. Landing anywhere
   * other than `refreshing` on the first step means the tap was swallowed, and the inner observable
   * completes without emitting — nothing is left waiting for a later, unrelated advance.
   *
   * Both steps are `take(1)` so each step consumes exactly one advance and stops. The outer
   * `switchMap` re-arms on every tap, so a second one replaces the first watch instead of stacking a
   * second toast behind it.
   */
  readonly toastFailedRetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.eventReceived),
      filter(({ event }) => event.type === 'refreshRequested' && event.trigger === 'retryRequested'),
      switchMap(() =>
        this.actions$.pipe(
          ofType(SessionActions.stateAdvanced),
          map(({ state }) => state),
          take(1),
          filter(state => state.tag === 'refreshing'),
          switchMap(() =>
            this.actions$.pipe(
              ofType(SessionActions.stateAdvanced),
              map(({ state }) => state),
              take(1),
              filter(state => state.tag === 'restoreDeferred')
            )
          ),
          map(() =>
            NotificationActions.showToast({
              options: createToast({ message: "Still can't connect.", type: 'negative' }),
            })
          )
        )
      )
    )
  );
}
